import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useMutation } from "@apollo/client/react";
import toast from "react-hot-toast";
import { UPDATE_DETALLE_LIQUIDACION } from "../../../data/graphQL/mutations/PreLiquidacion/updateDetalleLiquidacion";

const ESTADO_APROBADO = "APROBADO";
const ESTADO_DESESTIMADO = "DESESTIMADO";
const SET_VACIO = new Set();

export const useSeleccionLiquidaciones = ({ onSuccess }) => {
    // Map<idProfesional, Set<idLiquidacionDetalle>>
    const [seleccionPorProfesional, setSeleccionPorProfesional] = useState(() => new Map());

    const seleccionRef = useRef(seleccionPorProfesional);
    useEffect(() => {
        seleccionRef.current = seleccionPorProfesional;
    }, [seleccionPorProfesional]);

    const [updateDetalle, { loading }] = useMutation(UPDATE_DETALLE_LIQUIDACION);

    // Cache de Sets por profesional para estabilizar referencias.
    // Si el Set de un profesional no cambió, devuelve el mismo objeto → memo no se rompe.
    const setsCache = useRef(new Map());

    const getSeleccionProfesional = useCallback(
        (idProfesional) => {
            const setActual = seleccionPorProfesional.get(idProfesional);

            if (!setActual) {
                // Sin selección: siempre devuelve el mismo objeto estable
                setsCache.current.delete(idProfesional);
                return SET_VACIO;
            }

            // Si la referencia del Set cambió, actualizamos el cache
            const cached = setsCache.current.get(idProfesional);
            if (cached !== setActual) {
                setsCache.current.set(idProfesional, setActual);
            }
            return setsCache.current.get(idProfesional);
        },
        [seleccionPorProfesional]
    );

    const toggleSeleccion = useCallback((idProfesional, idLiquidacion) => {
        setSeleccionPorProfesional(prev => {
            const next = new Map(prev);
            const setActual = next.get(idProfesional) ?? new Set();
            const nuevoSet = new Set(setActual);

            if (nuevoSet.has(idLiquidacion)) {
                nuevoSet.delete(idLiquidacion);
            } else {
                nuevoSet.add(idLiquidacion);
            }

            if (nuevoSet.size === 0) {
                next.delete(idProfesional);
            } else {
                next.set(idProfesional, nuevoSet);
            }

            return next;
        });
    }, []);

    const seleccionarTodos = useCallback((idProfesional, liquidacionesElegibles) => {
        setSeleccionPorProfesional(prev => {
            const next = new Map(prev);
            const setActual = next.get(idProfesional) ?? new Set();
            const ids = liquidacionesElegibles.map(l => l.idLiquidacionProfesionalDetalle);
            const todosSeleccionados = ids.every(id => setActual.has(id));

            if (todosSeleccionados) {
                next.delete(idProfesional);
            } else {
                next.set(idProfesional, new Set(ids));
            }

            return next;
        });
    }, []);

    const limpiarSeleccion = useCallback(() => {
        setsCache.current.clear();
        setSeleccionPorProfesional(new Map());
    }, []);

    const cantidadSeleccionada = useMemo(() => {
        let total = 0;
        for (const set of seleccionPorProfesional.values()) {
            total += set.size;
        }
        return total;
    }, [seleccionPorProfesional]);

    const ejecutarAccion = async (estadoAColocar, justificacion = null) => {
        const ids = [];
        for (const set of seleccionRef.current.values()) {
            for (const id of set) ids.push(id);
        }

        if (ids.length === 0) {
            toast.error('Debe seleccionar al menos un detalle de liquidación');
            return false;
        }

        const inputs = ids.map(idLiquidacionDetalle => ({
            idLiquidacionDetalle,
            estadoAColocar,
            justificacion
        }));

        try {
            await updateDetalle({ variables: { inputs } });

            const mensaje = estadoAColocar === ESTADO_APROBADO
                ? `${ids.length} liquidación(es) aprobada(s) correctamente`
                : `${ids.length} liquidación(es) desestimada(s) correctamente`;

            toast.success(mensaje);
            limpiarSeleccion();
            onSuccess?.();
            return true;
        } catch (e) {
            toast.error('Ocurrió un error al procesar las liquidaciones');
            console.error(e);
            return false;
        }
    };

    const aprobar = () => ejecutarAccion(ESTADO_APROBADO);
    const desestimar = (justificacion) => ejecutarAccion(ESTADO_DESESTIMADO, justificacion);

    return {
        getSeleccionProfesional,
        toggleSeleccion,
        seleccionarTodos,
        limpiarSeleccion,
        aprobar,
        desestimar,
        loading,
        cantidadSeleccionada
    };
};